import { Controller } from '@hotwired/stimulus';
import { callTrigger } from '../trigger-actions';
import { compare, convertBracketToDotNotation, convertDotToBracketNotation, getFieldValue, isFieldReference } from '../utilities/form';

function getNameLevelUp(name, levels = 1) {
    return convertBracketToDotNotation(name).split('.').slice(0, -levels).join('.');
}

function getSourceName(source, currentName) {
    if (source.startsWith('~.')) {
        const level = getNameLevelUp(currentName, 1);

        return convertDotToBracketNotation(level ? `${level}.${source.substring(2)}` : source.substring(2));
    }

    if (source.startsWith('@')) {
        return convertDotToBracketNotation(source.substring(1));
    }

    return convertDotToBracketNotation(source);
}

class Condition {
    constructor(trigger, condition) {
        this._trigger = trigger;

        if (condition.conditions) {
            this.conditions = condition.conditions.map(entry => new Condition(this._trigger, entry))
            this.boolean = condition.boolean;
        } else {
            this.reference = condition.reference;
            this.operator = condition.operator;
            this.type = condition.type;
            this.value = condition.value;

            if (isFieldReference(condition.reference)) {
                this._trigger.watch(condition.reference);
            }
        }
    }

    validate() {
        if (this.conditions) {
            if (this.boolean === 'and') {
                return this.conditions.every(condition => condition.validate());
            }

            return this.conditions.some(condition => condition.validate());
        }

        return this._validate();
    }

    _validate() {
        if (this.conditions) {
            throw new Error('_validate should not be called with sub-conditions');
        }

        if (this.operator === 'changed') {
            // Check if the changed input is the same as the current condition references
            return this._trigger.changedFieldMatches(this.reference);
        }

        const leftValue = getFieldValue(this._trigger.getField(this.reference));
        const rightValue = { type: this.type, value: this.value };

        return compare(leftValue, this.operator, rightValue, this.type);
    }
}

class Trigger {
    constructor(rootEl, el, currentField, trigger) {
        this._rootEl = rootEl;
        this._el = el;
        this._currentField = currentField;
        this._referenceMap = new Map();
        this._changedInput = null;
        this.watching = new Set();
        this.group = new Condition(this, trigger.group);
        this.truthyActions = trigger.truthyActions;
        this.falsyActions = trigger.falsyActions;
        this.triggerOnLoad = trigger.triggerOnLoad;
    }

    watch(ref) {
        this.watching.add(ref);
    }

    isWatching(fieldName) {
        for (const source of this.watching) {
            if (getSourceName(source, this._currentField.name) === fieldName) {
                return true;
            }
        }

        return false;
    }

    execute(changedInput = null) {
        this._changedInput = changedInput;

        if (this.group.validate()) {
            for (const action of this.truthyActions) {
                callTrigger(this, action, this._el, changedInput);
            }
        } else {
            for (const action of this.falsyActions) {
                callTrigger(this, action, this._el, changedInput);
            }
        }

        // Release changed input for garbage collection
        this._changedInput = null;
    }

    changedFieldMatches(name) {
        if (!this._changedInput?.name) {
            return false;
        }

        return getSourceName(this._changedInput.name, this._currentField.name) === getSourceName(name, this._currentField.name);
    }

    getField(ref) {
        if (!this._referenceMap.has(ref)) {
            const name = getSourceName(ref, this._currentField.name);

            this._referenceMap.set(ref, this._rootEl.querySelector(`[name="${name}"]`));
        }

        return this._referenceMap.get(ref);
    }
}

export default class extends Controller {
    static targets = ['input'];

    initialize() {
        super.initialize();
        this.triggers = [];
        this.formController = null;
    }

    connect() {
        const triggers = JSON.parse(this.element.dataset.triggers || 'null');
        if (!triggers || !triggers.length) {
            return;
        }

        const form = this.element.closest('form');
        if (!form) {
            return;
        }

        this.formController = Stimulus.getControllerForElementAndIdentifier(form, 'form');
        if (!this.formController) {
            return;
        }

        this.triggers = triggers.map(trigger => new Trigger(form, this.element, this.inputTarget, trigger));

        this.formController.addListener(this);

        this.triggers.forEach(trigger => {
            if (trigger.triggerOnLoad) {
                trigger.execute();
            }
        });
    }

    fieldChanged(input) {
        if (!this.triggers.length) {
            return;
        }

        this.triggers.forEach(trigger => {
            if (!trigger.isWatching(input.name)) {
                return;
            }

            trigger.execute(input);
        });
    }
}
